/**
 * AdminPlans — CRUD UI for the new 4-plan catalog
 * (trial / founding / quarterly / annual + any custom plans).
 *
 * New fields handled here:
 *   - billing_cycle (trial | quarter | year | month)
 *   - cycle_days (int)
 *   - badge (founding | popular | best_value | null)
 *   - is_founding (bool)
 *   - max_seats (int | null)
 *   - max_customers / max_loans / max_users (int | null = unlimited)
 *   - visible (bool)  → controls whether the plan appears on the public Subscription page
 */
import React, { useEffect, useState } from "react";
import { api, fmtPKR } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../../components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../../components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
  AlertDialogTrigger,
} from "../../components/ui/alert-dialog";
import { Plus, Pencil, Trash2, Package, Crown, Flame, Sparkles, EyeOff } from "lucide-react";
import { toast } from "sonner";

const CYCLE_OPTIONS = [
  { id: "trial", label: "Trial (no billing)", default_days: 30 },
  { id: "month", label: "Monthly", default_days: 30 },
  { id: "quarter", label: "Quarterly", default_days: 90 },
  { id: "year", label: "Annual", default_days: 365 },
];

const BADGE_OPTIONS = [
  { id: "none", label: "— no badge —" },
  { id: "founding", label: "🏆 Founding" },
  { id: "popular", label: "🔥 Most Popular" },
  { id: "best_value", label: "💰 Best Value" },
];

const BADGE_TONE = {
  founding: "bg-amber-500/15 text-amber-300 border-amber-500/30",
  popular: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  best_value: "bg-sky-500/15 text-sky-300 border-sky-500/30",
};

const BADGE_ICON = { founding: Crown, popular: Flame, best_value: Sparkles };

const emptyForm = {
  id: "",
  name: "",
  price_pkr: 0,
  billing_cycle: "quarter",
  cycle_days: 90,
  badge: "none",
  is_founding: false,
  max_seats: "",
  max_customers: "",
  max_loans: "",
  max_users: "",
  features: "",
  visible: true,
};

function unlimitedLabel(v) {
  return v == null ? "Unlimited" : Number(v).toLocaleString();
}

function cycleSuffix(p) {
  return { quarter: " / quarter", year: " / year", month: " / month", trial: "" }[p.billing_cycle] || "";
}

export default function AdminPlans() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/admin/plans").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);

  const openNew = () => {
    setEditingId(null);
    setForm(emptyForm);
    setOpen(true);
  };

  const openEdit = (p) => {
    setEditingId(p.id);
    setForm({
      id: p.id,
      name: p.name || "",
      price_pkr: p.price_pkr || 0,
      billing_cycle: p.billing_cycle || "quarter",
      cycle_days: p.cycle_days || 90,
      badge: p.badge || "none",
      is_founding: !!p.is_founding,
      max_seats: p.max_seats == null ? "" : String(p.max_seats),
      max_customers: p.max_customers == null ? "" : String(p.max_customers),
      max_loans: p.max_loans == null ? "" : String(p.max_loans),
      max_users: p.max_users == null ? "" : String(p.max_users),
      features: (p.features || []).join("\n"),
      visible: p.visible !== false,
    });
    setOpen(true);
  };

  const changeCycle = (cycle) => {
    const preset = CYCLE_OPTIONS.find((c) => c.id === cycle);
    setForm((f) => ({
      ...f,
      billing_cycle: cycle,
      cycle_days: preset ? preset.default_days : f.cycle_days,
    }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    const numOrNull = (v) => (v === "" || v == null ? null : Number(v));
    const payload = {
      ...(editingId ? {} : { id: form.id.trim() }),
      name: form.name.trim(),
      price_pkr: Number(form.price_pkr || 0),
      billing_cycle: form.billing_cycle,
      cycle_days: Number(form.cycle_days || 30),
      badge: form.badge === "none" ? null : form.badge,
      is_founding: !!form.is_founding,
      max_seats: numOrNull(form.max_seats),
      max_customers: numOrNull(form.max_customers),
      max_loans: numOrNull(form.max_loans),
      max_users: numOrNull(form.max_users),
      features: (form.features || "")
        .split("\n").map((s) => s.trim()).filter(Boolean),
      visible: !!form.visible,
    };
    setBusy(true);
    try {
      if (editingId) {
        await api.put(`/admin/plans/${editingId}`, payload);
        toast.success("Plan updated");
      } else {
        if (!payload.id) {
          toast.error("Plan ID required");
          setBusy(false);
          return;
        }
        await api.post("/admin/plans", payload);
        toast.success("Plan created");
      }
      setOpen(false);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };

  const del = async (pid) => {
    try {
      await api.delete(`/admin/plans/${pid}`);
      toast.success("Plan deleted");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-plans-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl font-bold text-white">Plans Catalog</h1>
          <p className="text-slate-400 mt-1">Subscription tiers available to shops. Drives pricing, billing cycle, badges & founding-spot tracking.</p>
        </div>
        <Button className="bg-amber-500 hover:bg-amber-600 text-slate-900 font-semibold" onClick={openNew} data-testid="admin-new-plan-btn">
          <Plus className="w-4 h-4 mr-1" /> New Plan
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {rows.map((p) => {
          const BadgeIcon = BADGE_ICON[p.badge];
          return (
            <div
              key={p.id}
              className={`bg-[#151A23] border rounded-md p-5 relative ${p.badge === "founding" ? "border-amber-500/40" : "border-slate-800"}`}
              data-testid={`admin-plan-${p.id}`}
            >
              {p.badge && (
                <div className={`absolute -top-2.5 left-4 inline-flex items-center gap-1 rounded-full ${BADGE_TONE[p.badge]} border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider`}>
                  {BadgeIcon && <BadgeIcon className="w-3 h-3" />}
                  {p.badge}
                </div>
              )}
              <div className="flex items-start justify-between">
                <div className="w-10 h-10 rounded-md bg-amber-500/10 border border-amber-500/30 flex items-center justify-center text-amber-300">
                  <Package className="w-5 h-5" />
                </div>
                <div className="flex gap-1">
                  {p.visible === false && (
                    <span className="inline-flex items-center gap-1 text-[10px] text-slate-500 px-1.5 py-0.5 border border-slate-700 rounded">
                      <EyeOff className="w-3 h-3" /> hidden
                    </span>
                  )}
                  <Button size="icon" variant="ghost" className="text-slate-400 hover:text-white" onClick={() => openEdit(p)} data-testid={`admin-plan-edit-${p.id}`}>
                    <Pencil className="w-4 h-4" />
                  </Button>
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button size="icon" variant="ghost" className="text-slate-400 hover:text-red-400" data-testid={`admin-plan-del-${p.id}`}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Delete plan "{p.name}"?</AlertDialogTitle>
                        <AlertDialogDescription>
                          Plans in use by any shop cannot be deleted. Default and founding plans are also protected.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction onClick={() => del(p.id)}>Delete</AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </div>
              <div className="mt-4">
                <div className="font-display text-2xl font-bold text-white">{p.name}</div>
                <div className="text-xs text-slate-500 font-mono">{p.id}</div>
              </div>
              <div className="mt-3 font-display text-3xl font-bold text-amber-300 tabular">
                {p.price_pkr === 0 ? "Free" : fmtPKR(p.price_pkr)}
                {p.price_pkr > 0 && <span className="text-sm text-slate-500 font-normal ml-1">{cycleSuffix(p)}</span>}
              </div>
              <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
                <div className="rounded bg-slate-900/50 border border-slate-800 px-2 py-1.5">
                  <div className="text-slate-500">Cycle</div>
                  <div className="font-semibold text-slate-200 capitalize">{p.billing_cycle} · {p.cycle_days}d</div>
                </div>
                <div className="rounded bg-slate-900/50 border border-slate-800 px-2 py-1.5">
                  <div className="text-slate-500">Seats cap</div>
                  <div className="font-semibold text-slate-200">{p.max_seats == null ? "∞" : p.max_seats}</div>
                </div>
              </div>
              <div className="mt-3 grid grid-cols-3 gap-2 text-xs">
                <Stat label="Customers" value={unlimitedLabel(p.max_customers)} />
                <Stat label="Loans" value={unlimitedLabel(p.max_loans)} />
                <Stat label="Users" value={unlimitedLabel(p.max_users)} />
              </div>
              {p.features?.length > 0 && (
                <ul className="mt-4 space-y-1 text-xs text-slate-400 max-h-32 overflow-y-auto pr-1">
                  {p.features.map((f) => (
                    <li key={`${p.id}-${f}`}>· {f}</li>
                  ))}
                </ul>
              )}
            </div>
          );
        })}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl bg-[#151A23] text-slate-200 border border-slate-800 max-h-[calc(100dvh-2rem)] overflow-y-auto" data-testid="admin-plan-dialog">
          <DialogHeader>
            <DialogTitle className="text-white">{editingId ? `Edit Plan · ${editingId}` : "New Plan"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label>Plan ID *</Label>
              <Input required disabled={!!editingId} value={form.id} onChange={(e) => setForm({ ...form, id: e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, "") })} placeholder="e.g. enterprise" className="bg-slate-900 border-slate-700" data-testid="admin-plan-id" />
            </div>
            <div className="space-y-1.5">
              <Label>Display Name *</Label>
              <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-slate-900 border-slate-700" data-testid="admin-plan-name" />
            </div>
            <div className="space-y-1.5">
              <Label>Billing Cycle *</Label>
              <Select value={form.billing_cycle} onValueChange={changeCycle}>
                <SelectTrigger className="bg-slate-900 border-slate-700" data-testid="admin-plan-cycle"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CYCLE_OPTIONS.map((c) => <SelectItem key={c.id} value={c.id}>{c.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Cycle days *</Label>
              <Input type="number" min={1} max={3650} value={form.cycle_days} onChange={(e) => setForm({ ...form, cycle_days: e.target.value })} className="bg-slate-900 border-slate-700" data-testid="admin-plan-cycle-days" />
            </div>
            <div className="space-y-1.5">
              <Label>Price (PKR per cycle)</Label>
              <Input type="number" min={0} value={form.price_pkr} onChange={(e) => setForm({ ...form, price_pkr: e.target.value })} className="bg-slate-900 border-slate-700" data-testid="admin-plan-price" />
            </div>
            <div className="space-y-1.5">
              <Label>Badge</Label>
              <Select value={form.badge} onValueChange={(v) => setForm({ ...form, badge: v })}>
                <SelectTrigger className="bg-slate-900 border-slate-700" data-testid="admin-plan-badge"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {BADGE_OPTIONS.map((b) => <SelectItem key={b.id} value={b.id}>{b.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Max Customers <span className="text-[10px] text-slate-500">(blank = unlimited)</span></Label>
              <Input type="number" min={0} value={form.max_customers} onChange={(e) => setForm({ ...form, max_customers: e.target.value })} className="bg-slate-900 border-slate-700" data-testid="admin-plan-max-customers" />
            </div>
            <div className="space-y-1.5">
              <Label>Max Loans <span className="text-[10px] text-slate-500">(blank = unlimited)</span></Label>
              <Input type="number" min={0} value={form.max_loans} onChange={(e) => setForm({ ...form, max_loans: e.target.value })} className="bg-slate-900 border-slate-700" data-testid="admin-plan-max-loans" />
            </div>
            <div className="space-y-1.5">
              <Label>Max Users <span className="text-[10px] text-slate-500">(blank = unlimited)</span></Label>
              <Input type="number" min={0} value={form.max_users} onChange={(e) => setForm({ ...form, max_users: e.target.value })} className="bg-slate-900 border-slate-700" data-testid="admin-plan-max-users" />
            </div>
            <div className="space-y-1.5">
              <Label>Founding Seats Cap <span className="text-[10px] text-slate-500">(blank = no cap)</span></Label>
              <Input type="number" min={0} value={form.max_seats} onChange={(e) => setForm({ ...form, max_seats: e.target.value })} className="bg-slate-900 border-slate-700" data-testid="admin-plan-max-seats" />
            </div>
            <div className="sm:col-span-2 flex items-center gap-4 flex-wrap">
              <label className="inline-flex items-center gap-2 text-sm cursor-pointer">
                <input type="checkbox" checked={form.is_founding} onChange={(e) => setForm({ ...form, is_founding: e.target.checked })} className="accent-amber-500 w-4 h-4" data-testid="admin-plan-is-founding" />
                <span>Founding plan (locks-in pricing forever)</span>
              </label>
              <label className="inline-flex items-center gap-2 text-sm cursor-pointer">
                <input type="checkbox" checked={form.visible} onChange={(e) => setForm({ ...form, visible: e.target.checked })} className="accent-emerald-500 w-4 h-4" data-testid="admin-plan-visible" />
                <span>Visible on public Subscription page</span>
              </label>
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <Label>Features (one per line)</Label>
              <textarea
                rows={6}
                value={form.features}
                onChange={(e) => setForm({ ...form, features: e.target.value })}
                placeholder={"Unlimited Customers\nUnlimited Loans\nDigital Agreements\n…"}
                className="w-full rounded-md bg-slate-900 border border-slate-700 text-sm p-2 text-slate-200 font-mono"
                data-testid="admin-plan-features"
              />
            </div>
            <DialogFooter className="sm:col-span-2">
              <Button type="submit" disabled={busy} className="bg-amber-500 hover:bg-amber-600 text-slate-900 font-semibold" data-testid="admin-plan-save-btn">
                {busy ? "Saving…" : editingId ? "Update Plan" : "Create Plan"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="rounded bg-slate-900/50 border border-slate-800 px-2 py-1.5">
      <div className="text-slate-500 text-[10px] uppercase">{label}</div>
      <div className="font-semibold text-slate-200 tabular text-xs">{value}</div>
    </div>
  );
}
